import { useRef, useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useMotionValue, useTransform, animate, useDragControls, type PanInfo } from "framer-motion";
import { X } from "lucide-react";
import { lockBodyScroll, unlockBodyScroll, BodyScrollLockToken } from "@/lib/capacitorScrollLock";
import { cn } from "@/lib/utils";

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  fullHeight?: boolean;
  contentClassName?: string;
  customHeader?: React.ReactNode;
  hideDefaultHeader?: boolean;
}

export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  fullHeight = false,
  contentClassName,
  customHeader,
  hideDefaultHeader = false,
}: BottomSheetProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const lockTokenRef = useRef<BodyScrollLockToken | null>(null);
  const [webKbOffset, setWebKbOffset] = useState(0);

  const dragY = useMotionValue(0);
  const dragControls = useDragControls();
  const backdropOpacity = useTransform(dragY, [0, 300], [1, 0.3]);

  const sheetOffset = webKbOffset > 0 ? `${webKbOffset}px` : 'var(--cap-kb-height, 0px)';
  const sheetHeight = fullHeight
    ? webKbOffset > 0
      ? `calc(100dvh - ${webKbOffset}px - env(safe-area-inset-top, 0px) - 12px)`
      : 'calc(100dvh - var(--cap-kb-height, 0px) - env(safe-area-inset-top, 0px) - 12px)'
    : webKbOffset > 0
      ? `calc(85vh - ${webKbOffset}px)`
      : 'calc(85vh - var(--cap-kb-height, 0px))';

  useEffect(() => {
    if (isOpen) {
      dragY.set(0);
    }
  }, [dragY, isOpen]);

  // Track visualViewport for mobile web keyboard offset
  useEffect(() => {
    if (!isOpen) return;
    const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
    if (isCapacitor) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const diff = window.innerHeight - vv.height;
      setWebKbOffset(diff > 50 ? diff : 0);
    };
    vv.addEventListener('resize', onResize);
    onResize();
    return () => {
      vv.removeEventListener('resize', onResize);
      setWebKbOffset(0);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Lock .app-root (Capacitor iOS) via token system
    lockTokenRef.current = lockBodyScroll();

    // Lock body/html for web/mobile browser
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      unlockBodyScroll(lockTokenRef.current);
      lockTokenRef.current = null;
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  const handleDragEnd = useCallback((_: any, info: PanInfo) => {
    if (info.offset.y > 60 || info.velocity.y > 300) {
      onClose();
    } else {
      animate(dragY, 0, { type: 'spring', stiffness: 600, damping: 30 });
    }
  }, [onClose, dragY]);

  const handleHeaderPointerDown = useCallback((event: any) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select, [data-no-drag]')) return;
    dragControls.start(event);
  }, [dragControls]);

  // Prevent background scroll bleed on backdrop
  const stopGestures = useCallback((e: React.TouchEvent | React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            onTouchMove={stopGestures}
            onWheel={stopGestures}
            className="fixed inset-0 z-[1000]"
            style={{
              opacity: backdropOpacity,
              background: 'hsl(0 0% 0% / 0.7)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
            className="fixed left-0 right-0 z-[1001]"
            style={{ bottom: sheetOffset }}
          >
            <motion.div
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.4 }}
              dragTransition={{ bounceStiffness: 600, bounceDamping: 30 }}
              dragMomentum={false}
              dragSnapToOrigin={false}
              onDragEnd={handleDragEnd}
              data-overlay-type="sheet"
              className="relative rounded-t-[22px] flex flex-col overflow-hidden"
              style={{
                y: dragY,
                background: 'hsl(var(--bg-app))',
                borderTop: '1px solid hsl(var(--gold) / 0.15)',
                boxShadow: '0 -12px 60px hsl(0 0% 0% / 0.6), inset 0 1px 0 hsl(var(--gold) / 0.08)',
                paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
                maxHeight: sheetHeight,
                height: fullHeight ? sheetHeight : undefined,
                overscrollBehavior: 'contain',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-32 rounded-t-[22px] pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse 60% 50% at 50% 0%, hsl(var(--gold) / 0.06) 0%, transparent 100%)',
                }}
              />

              <div
                className="relative z-10 cursor-grab active:cursor-grabbing select-none"
                onPointerDown={handleHeaderPointerDown}
                style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
              >
                <div className="flex justify-center pt-3 pb-1">
                  <div className="w-10 h-1 rounded-full" style={{ background: 'hsl(var(--gold) / 0.25)' }} />
                </div>

                {customHeader ? (
                  <div>{customHeader}</div>
                ) : !hideDefaultHeader ? (
                  <div className="flex items-center justify-between px-5 pb-3">
                    <h2
                      className="font-sans text-[11px] font-semibold uppercase tracking-[0.2em]"
                      style={{ color: 'hsl(var(--gold) / 0.7)' }}
                    >
                      {title}
                    </h2>
                    <button
                      onClick={onClose}
                      className="w-7 h-7 flex items-center justify-center rounded-full transition-colors"
                      style={{
                        background: 'hsl(var(--ivory) / 0.06)',
                        border: '1px solid hsl(var(--gold) / 0.10)',
                      }}
                      aria-label="Close"
                    >
                      <X className="w-3.5 h-3.5" style={{ color: 'hsl(var(--ivory) / 0.45)' }} />
                    </button>
                  </div>
                ) : null}
              </div>

              <div
                ref={contentRef}
                className={cn('px-5 pb-4 relative z-10 min-h-0', footer && 'pb-3', contentClassName)}
                style={{
                  flex: fullHeight ? '1 1 auto' : '0 1 auto',
                  overflowY: contentClassName?.includes('overflow-hidden') ? 'hidden' : 'auto',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehavior: 'contain',
                  touchAction: 'pan-y',
                  display: contentClassName?.includes('flex') ? 'flex' : undefined,
                  flexDirection: contentClassName?.includes('flex-col') ? 'column' : undefined,
                }}
              >
                {children}
              </div>
              {footer && (
                <div
                  className="px-5 pt-3 pb-1 relative z-10 shrink-0"
                  style={{
                    borderTop: '1px solid hsl(var(--gold) / 0.08)',
                    background: 'linear-gradient(180deg, hsl(var(--bg-app) / 0.92), hsl(var(--bg-app)))',
                  }}
                >
                  {footer}
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};